import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  AvailableChannels,
  ChannelOptions,
  WidgetConfigResponse,
} from '@/types/widgetConfig';
import { axiosClient } from '@/utils/apiClient';
import useWidgetDataAttr from './useWidgetDataAttr';

// React Query hook for widget configuration
export const useWidgetConfig = () => {
  const { widgetId, companyId, location } = useWidgetDataAttr();

  const query = useQuery<WidgetConfigResponse, Error>({
    queryKey: ['widgetConfig', widgetId],
    queryFn: async (): Promise<WidgetConfigResponse> => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const { data } = await axiosClient.get<WidgetConfigResponse>(
        `/LiveChatV2/Public/Configs?channelIdentityId=${widgetId}`,
      );
      return data;
    },
    enabled: !!widgetId && !!companyId && !!location, // Only fetch when widgetId is available
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Memoized computed values - Updated for V2 structure
  const welcomeMessage = useMemo(() => {
    const config = query.data;

    // V2: Welcome message comes from additionalSettings.welcomeMessage
    return (
      config?.settings.additionalSettings.welcomeMessage ||
      'Welcome to our support! How can we help you today?'
    );
  }, [query.data]);

  const popupMessages = useMemo(() => {
    return query.data?.settings.popupMessages || [];
  }, [query.data]);

  const displayLogic = useMemo(
    () => ({
      buttonColor: query.data?.settings.displayLogic.buttonColor || '#0066ff',
      messageBackgroundColor:
        query.data?.settings.displayLogic.messageBackgroundColor || '#0D122C',
      widgetPosition:
        query.data?.settings.displayLogic.widgetPosition || 'bottom-right',
    }),
    [query.data],
  );

  const branding = useMemo(
    () => ({
      companyLogoUrl: query.data?.settings.branding.companyLogoUrl || '',
      widgetDisplayName:
        query.data?.settings.branding.widgetDisplayName || 'Support',
      senderDisplayName:
        query.data?.settings.branding.senderDisplayName || 'Support Agent',
    }),
    [query.data],
  );

  const languageConfig = useMemo(
    () => ({
      availableLanguages: query.data?.settings.languages.availableLanguage || {
        en: true,
      },
      defaultLanguage: query.data?.settings.languages.defaultLanguage || 'en',
    }),
    [query.data],
  );

  const channelConfig = useMemo(
    () => ({
      availableChannels: (query.data?.settings.channels.availableChannels ||
        {}) as AvailableChannels,
      options: (query.data?.settings.channels.options || {}) as ChannelOptions,
    }),
    [query.data],
  );

  const preChatFormConfig = useMemo(
    () => ({
      enabled: query.data?.settings.preChatForm.enabled || false,
      isEmailRequired:
        query.data?.settings.preChatForm.isEmailRequired || false,
      isPhoneNumberRequired:
        query.data?.settings.preChatForm.isPhoneNumberRequired || false,
    }),
    [query.data],
  );

  const additionalSettings = useMemo(
    () => ({
      welcomeMessage:
        query.data?.settings.additionalSettings.welcomeMessage ||
        'Welcome to our support! How can we help you today?',
    }),
    [query.data],
  );

  return {
    // React Query state
    config: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,

    // Computed values (memoized)
    welcomeMessage,
    branding,
    displayLogic,
    languageConfig,
    channelConfig,
    preChatFormConfig,
    additionalSettings,
    popupMessages,
  };
};
